import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TableCell, TableRow } from '@/components/ui/table';
import { DataTableEnhanced, type ColumnDef, type ColumnSort } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray, type PaginationMetadata } from '@/types/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Settings2, Edit, Download, History, Receipt, Clock, Plus, UserPlus, CalendarPlus } from 'lucide-react';
import { mockRoleCoverage } from '@/lib/mock-scheduling-data';
import { useSchedulerConnection } from '@/hooks/use-scheduler-connection';
import { ColumnConfiguratorDrawer } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { RateCardSheet } from '@/components/RateCard';
import { AssignmentSheet } from '@/components/assignment-sheet';
import { 
  RESOURCE_BUDGET_COLUMNS, 
  RESOURCE_BUDGET_PRESETS,
  RESOURCE_BUDGET_STORAGE_KEY 
} from '@/lib/resource-budget-columns';
import { 
  formatHours, 
  formatCurrency, 
  formatPercent,
  getResourceBudgetStatus,
  getResourceBudgetStatusLabel,
  getResourceBudgetStatusColor,
  getTeamAllocationStatus,
  getTeamAllocationStatusLabel,
  getTeamAllocationStatusColor
} from '@/lib/budget-utils';
import type { ResourceBudget } from '@/types/budget.types';
import type { Employee, ResourceAllocation } from '@/types';
import type { AssignmentSelection } from '@/types/assignment.types';

interface ResourceBudgetViewProps {
  projectId: number;
  data: ResourceBudget;
  onEdit?: () => void;
  onExport?: () => void;
  onHistory?: () => void;
  availablePeople?: Employee[];
  existingAllocations?: ResourceAllocation[];
  projectStartDate?: string;
  projectEndDate?: string;
  onAssign?: (role: string, selections: AssignmentSelection[]) => void;
  onRaiseRequest?: (roleName?: string) => void;
  /** Set of role names that have a pending request this session */
  pendingRequestRoles?: Set<string>;
}

export function ResourceBudgetView({ projectId, data, onEdit, onExport, onHistory, availablePeople, existingAllocations, projectStartDate, projectEndDate, onAssign, onRaiseRequest, pendingRequestRoles }: ResourceBudgetViewProps) {
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);
  const [assignSheetOpen, setAssignSheetOpen] = useState(false);
  const [assignRoleName, setAssignRoleName] = useState('');
  const [assignRequiredHours, setAssignRequiredHours] = useState(0);

  console.log('[ResourceBudgetView] Received projectId:', projectId, 'type:', typeof projectId);

  if (!data || !data.summary || !data.byRole) {
    console.log('[ResourceBudgetView] Missing required data:', {
      hasData: !!data,
      hasSummary: !!data?.summary,
      hasByRole: !!data?.byRole,
    });
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Card className="w-full max-w-md border-dashed">
          <CardContent className="flex flex-col items-center justify-center text-center p-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Resource Budget</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Plan team capacity and track hours by creating a resource budget for this project.
            </p>
            {onEdit && (
              <Button onClick={onEdit}>
                <Plus className="mr-2 h-4 w-4" />
                Create Resource Budget
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!projectId) {
    console.error('[ResourceBudgetView] Missing projectId!');
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Invalid project ID</p>
      </div>
    );
  }

  const { summary, byRole, byPerson } = data;
  console.log('[ResourceBudgetView] Summary:', summary);
  console.log('[ResourceBudgetView] ByRole count:', byRole.length);

  const { isConnected: schedulerConnected } = useSchedulerConnection();

  const handleAssignClick = (roleName: string, remainingHours: number) => {
    // When scheduler is connected, the Actions column raises a request instead — 
    // this handler should never be called in that mode, but guard anyway.
    if (schedulerConnected) return;
    setAssignRoleName(roleName);
    setAssignRequiredHours(remainingHours);
    setAssignSheetOpen(true);
  };

  const handleAssignConfirm = (selections: AssignmentSelection[]) => {
    if (onAssign) {
      onAssign(assignRoleName, selections);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Resource Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}

            <ColumnConfigButton />
          </div>
        </CardHeader>
        <CardContent>
          <HoursByRoleView
            data={byRole}
            schedulerConnected={schedulerConnected}
            onAssignClick={!schedulerConnected && onAssign ? handleAssignClick : undefined}
            onRaiseRequest={schedulerConnected ? onRaiseRequest : undefined}
            pendingRequestRoles={pendingRequestRoles}
          />
        </CardContent>
      </Card>

      {/* Rate Card Sheet */}
      <RateCardSheet
        open={isRateCardOpen}
        onOpenChange={setIsRateCardOpen}
        projectId={String(projectId)}
      />

      {/* Assignment Sheet — only shown when scheduler is NOT connected */}
      {!schedulerConnected && availablePeople && existingAllocations && projectStartDate && projectEndDate && (
        <AssignmentSheet
          open={assignSheetOpen}
          onOpenChange={setAssignSheetOpen}
          roleName={assignRoleName}
          requiredHours={assignRequiredHours}
          projectId={projectId}
          projectStartDate={projectStartDate}
          projectEndDate={projectEndDate}
          availablePeople={availablePeople}
          existingAllocations={existingAllocations}
          onConfirm={handleAssignConfirm}
        />
      )}
    </div>
  );
}

// Column Configuration Button Component
function ColumnConfigButton() {
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const { columns, updateColumns } = useColumnPreferences({
    storageKey: RESOURCE_BUDGET_STORAGE_KEY,
    defaultColumns: RESOURCE_BUDGET_COLUMNS,
  });

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsConfigOpen(true)}
        className="gap-2"
      >
        <Settings2 className="h-4 w-4" />
        Configure Columns
      </Button>
      <ColumnConfiguratorDrawer
        open={isConfigOpen}
        onOpenChange={setIsConfigOpen}
        columns={columns}
        onSave={updateColumns}
        presets={RESOURCE_BUDGET_PRESETS}
        defaultColumns={RESOURCE_BUDGET_COLUMNS}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Scheduling Status helpers (for the Actions column raise-request logic)
// ---------------------------------------------------------------------------

type SchedulingStatusDisplay =
  | { kind: 'pending' }
  | { kind: 'full' }
  | { kind: 'partial'; unmetHours: number }
  | { kind: 'none' };

function getSchedulingStatusDisplay(
  roleName: string,
  pendingRequestRoles?: Set<string>,
): SchedulingStatusDisplay {
  // Session-level pending request takes priority
  if (pendingRequestRoles?.has(roleName)) return { kind: 'pending' };

  const coverage = mockRoleCoverage.find((r) => r.roleName === roleName);
  if (!coverage) return { kind: 'none' };

  // Mock data pending flag
  if (coverage.hasPendingRequest) return { kind: 'pending' };

  if (coverage.coveragePercent >= 100) return { kind: 'full' };

  const unmetHours = Math.round(coverage.budgetHours - coverage.scheduledHours);
  if (coverage.coveragePercent >= 50) return { kind: 'partial', unmetHours };

  return { kind: 'none' };
}

// ---------------------------------------------------------------------------
// Budget by Role View
// ---------------------------------------------------------------------------
function HoursByRoleView({
  data,
  schedulerConnected,
  onAssignClick,
  onRaiseRequest,
  pendingRequestRoles,
}: {
  data: ResourceBudget['byRole'];
  schedulerConnected?: boolean;
  onAssignClick?: (roleName: string, remainingHours: number) => void;
  onRaiseRequest?: (roleName?: string) => void;
  pendingRequestRoles?: Set<string>;
}) {
  const { columns, isLoading } = useColumnPreferences({
    storageKey: RESOURCE_BUDGET_STORAGE_KEY,
    defaultColumns: RESOURCE_BUDGET_COLUMNS,
  });

  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Columns to suppress when a scheduler is connected
  const SCHEDULER_HIDDEN_COLUMNS = ['allocated-hours'];

  // Get visible columns in order (safe to compute even while loading)
  const visibleColumns = useMemo(
    () => columns
      .filter((col) => col.visible)
      .filter((col) => !(schedulerConnected && SCHEDULER_HIDDEN_COLUMNS.includes(col.id)))
      .sort((a, b) => a.order - b.order),
    [columns, schedulerConnected],
  );

  const rows = useMemo(() => (data ?? []).map((role) => {
    const status = getResourceBudgetStatus(role.actualHours, role.budgetHours);
    const statusColors = getResourceBudgetStatusColor(status);
    const statusLabel = getResourceBudgetStatusLabel(status);
    const varianceHours = role.actualHours - role.budgetHours;
    
    const teamAllocationStatus = getTeamAllocationStatus(role.allocatedHours || 0, role.budgetHours);
    const teamAllocationColors = getTeamAllocationStatusColor(teamAllocationStatus);
    const teamAllocationLabel = getTeamAllocationStatusLabel(teamAllocationStatus);
    
    return {
      id: role.role,
      role: role.role,
      'budget-hours': formatHours(role.budgetHours),
      'allocated-hours': formatHours(role.allocatedHours || 0),
      'team-allocation': { label: teamAllocationLabel, colors: teamAllocationColors },
      'actual-hours': formatHours(role.actualHours),
      'remaining-hours': formatHours(role.remainingHours),
      'percent-used': formatPercent(role.percentUsed),
      status: { label: statusLabel, colors: statusColors },
      'variance-hours': formatHours(varianceHours),
      'forecast-hours': formatHours(role.budgetHours), // Placeholder - would need actual forecast data
      efficiency: '100%', // Placeholder - would need actual efficiency calculation
      // Raw values for totals calculation
      _raw: {
        budgetHours: role.budgetHours,
        allocatedHours: role.allocatedHours || 0,
        actualHours: role.actualHours,
        remainingHours: role.remainingHours,
        varianceHours,
      },
    };
  }), [data]);

  // Calculate totals from raw values
  const totals = useMemo(() => rows.reduce(
    (acc, row) => ({
      budgetHours: acc.budgetHours + row._raw.budgetHours,
      allocatedHours: acc.allocatedHours + row._raw.allocatedHours,
      actualHours: acc.actualHours + row._raw.actualHours,
      remainingHours: acc.remainingHours + row._raw.remainingHours,
      varianceHours: acc.varianceHours + row._raw.varianceHours,
    }),
    { 
      budgetHours: 0,
      allocatedHours: 0,
      actualHours: 0, 
      remainingHours: 0, 
      varianceHours: 0,
    }
  ), [rows]);

  // Helper function to render cell content based on column ID
  const renderCell = (columnId: string, row: typeof rows[0]) => {
    if (columnId === 'status') {
      return (
        <Badge className={`${row.status.colors.badge} pointer-events-none`}>
          {row.status.label}
        </Badge>
      );
    }
    
    if (columnId === 'team-allocation') {
      return (
        <Badge className={`${row['team-allocation'].colors.badge} pointer-events-none`}>
          {row['team-allocation'].label}
        </Badge>
      );
    }
    
    // Color code negative values for remaining and variance
    if (columnId === 'remaining-hours' && row._raw.remainingHours < 0) {
      return <span className="text-destructive font-semibold">{row[columnId]}</span>;
    }
    
    if (columnId === 'variance-hours' && row._raw.varianceHours !== 0) {
      const isNegative = row._raw.varianceHours < 0;
      return (
        <span className={isNegative ? 'text-destructive font-semibold' : 'text-green-600 font-semibold'}>
          {row[columnId]}
        </span>
      );
    }
    
    return row[columnId as keyof typeof row] as string;
  };

  // Helper function to render total cell content based on column ID
  const renderTotalCell = (columnId: string) => {
    switch (columnId) {
      case 'role':
        return 'TOTAL';
      case 'budget-hours':
        return formatHours(totals.budgetHours);
      case 'allocated-hours':
        return formatHours(totals.allocatedHours);
      case 'team-allocation':
        const totalTeamAllocationStatus = getTeamAllocationStatus(totals.allocatedHours, totals.budgetHours);
        return (
          <Badge className={`${getTeamAllocationStatusColor(totalTeamAllocationStatus).badge} pointer-events-none`}>
            {getTeamAllocationStatusLabel(totalTeamAllocationStatus)}
          </Badge>
        );
      case 'actual-hours':
        return formatHours(totals.actualHours);
      case 'remaining-hours':
        return totals.remainingHours < 0 ? (
          <span className="text-destructive font-semibold">{formatHours(totals.remainingHours)}</span>
        ) : (
          formatHours(totals.remainingHours)
        );
      case 'percent-used':
        return formatPercent((totals.actualHours / totals.budgetHours) * 100);
      case 'variance-hours':
        return totals.varianceHours !== 0 ? (
          <span className={totals.varianceHours < 0 ? 'text-destructive font-semibold' : 'text-green-600 font-semibold'}>
            {formatHours(totals.varianceHours)}
          </span>
        ) : (
          formatHours(totals.varianceHours)
        );
      case 'forecast-hours':
        return formatHours(totals.budgetHours);
      case 'status':
        const totalStatus = getResourceBudgetStatus(totals.actualHours, totals.budgetHours);
        return (
          <Badge className={`${getResourceBudgetStatusColor(totalStatus).badge} pointer-events-none`}>
            {getResourceBudgetStatusLabel(totalStatus)}
          </Badge>
        );
      default:
        return '';
    }
  };

  // Helper function to get alignment class
  const getAlignmentClass = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (columnId === 'status' || columnId === 'team-allocation') return 'text-center';
    return column?.align === 'right' ? 'text-right' : '';
  };

  // Determine unfulfilled hours for assignment (budget - allocated)
  const getUnfulfilledHours = (row: typeof rows[0]) => {
    return row._raw.budgetHours - row._raw.allocatedHours;
  };

  // Show the Actions column when either assign (no scheduler) or raise request (scheduler) is available
  const showActionsColumn = !!onAssignClick || (!!schedulerConnected && !!onRaiseRequest);

  // Sort rows — use _raw numeric values for numeric columns to avoid stringifying objects
  const sortedRows = useMemo(() => {
    const numericRawCols: Record<string, keyof typeof rows[0]['_raw']> = {
      'budget-hours': 'budgetHours',
      'allocated-hours': 'allocatedHours',
      'actual-hours': 'actualHours',
      'remaining-hours': 'remainingHours',
      'variance-hours': 'varianceHours',
    };
    if (columnSorts.length === 0) return rows;
    return [...rows].sort((a, b) => {
      for (const sort of columnSorts) {
        let cmp = 0;
        if (numericRawCols[sort.columnId]) {
          cmp = a._raw[numericRawCols[sort.columnId]] - b._raw[numericRawCols[sort.columnId]];
        } else {
          const aVal = String((a as any)[sort.columnId] ?? '');
          const bVal = String((b as any)[sort.columnId] ?? '');
          cmp = aVal.localeCompare(bVal);
        }
        if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [rows, columnSorts]);

  // Pagination
  const pagination: PaginationMetadata = useMemo(
    () => calculatePagination(sortedRows.length, currentPage, itemsPerPage),
    [sortedRows.length, currentPage, itemsPerPage],
  );
  const paginatedRows = useMemo(
    () => paginateArray(sortedRows, currentPage, itemsPerPage).data,
    [sortedRows, currentPage, itemsPerPage],
  );

  // Build ColumnDef array from visible columns + optional Actions column
  const columnDefs = useMemo((): ColumnDef<typeof rows[0], unknown>[] => {
    const defs: ColumnDef<typeof rows[0], unknown>[] = visibleColumns.map((col) => ({
      id: col.id,
      accessorKey: col.id,
      header: col.name,
      enableSorting: col.id !== 'status' && col.id !== 'team-allocation',
    }));
    if (showActionsColumn) {
      defs.push({ id: 'actions', header: 'Actions', enableSorting: false });
    }
    return defs;
  }, [visibleColumns, showActionsColumn]);

  // Early returns AFTER all hooks
  if (isLoading) {
    return <p className="text-muted-foreground text-center py-8">Loading...</p>;
  }
  if (!data || data.length === 0) {
    return <p className="text-muted-foreground text-center py-8">No role data available</p>;
  }

  return (
    <TooltipProvider>
      <DataTableEnhanced
        columns={columnDefs}
        data={paginatedRows}
        columnSorts={columnSorts}
        onColumnSortChange={(newSorts) => {
          setColumnSorts(newSorts);
          setCurrentPage(1);
        }}
        customRowRenderer={(row) => {
          const unfulfilledHours = getUnfulfilledHours(row.original);
          const schedulingStatus = schedulerConnected
            ? getSchedulingStatusDisplay(row.original.role, pendingRequestRoles)
            : null;
          const isCoverageComplete = schedulingStatus?.kind === 'full';
          const isPending = schedulingStatus?.kind === 'pending';

          return (
            <TableRow
              key={row.original.id}
              className="odd:bg-white even:bg-neutral-50 hover:bg-neutral-100"
            >
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.id}
                  className={`${col.id === 'role' ? 'font-medium' : ''} ${getAlignmentClass(col.id)}`}
                >
                  {renderCell(col.id, row.original)}
                </TableCell>
              ))}
              {showActionsColumn && (
                <TableCell className="text-center">
                  {schedulerConnected && onRaiseRequest ? (
                    isPending ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex">
                            <Button variant="outline" size="sm" disabled className="gap-1.5 opacity-50 cursor-not-allowed">
                              <CalendarPlus className="h-3.5 w-3.5" />
                              Request Pending
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-center">
                          A resource request for this role is already pending in Access Planner.
                        </TooltipContent>
                      </Tooltip>
                    ) : isCoverageComplete ? (
                      <Button variant="outline" size="sm" disabled className="gap-1.5 opacity-50 cursor-not-allowed">
                        <CalendarPlus className="h-3.5 w-3.5" />
                        Fully Scheduled
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRaiseRequest(row.original.role)}
                        className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        <CalendarPlus className="h-3.5 w-3.5" />
                        Raise Request
                      </Button>
                    )
                  ) : (
                    onAssignClick && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onAssignClick(row.original.role, unfulfilledHours)}
                        disabled={unfulfilledHours <= 0}
                        className="gap-1.5"
                      >
                        <UserPlus className="h-3.5 w-3.5" />
                        Assign
                      </Button>
                    )
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        }}
        footerContent={
          <TableRow className="font-bold bg-muted/50">
            {visibleColumns.map((col) => (
              <TableCell key={col.id} className={getAlignmentClass(col.id)}>
                {renderTotalCell(col.id)}
              </TableCell>
            ))}
            {showActionsColumn && <TableCell />}
          </TableRow>
        }
        paginationContent={
          <PaginationControls
            pagination={pagination}
            onPageChange={(p) => setCurrentPage(p)}
            onPageSizeChange={(s) => { setItemsPerPage(s); setCurrentPage(1); }}
          />
        }
      />
    </TooltipProvider>
  );
}
